import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import { ConfirmationDialog } from "../../utils/ConfirmationDialog";
import {
  AdminLeadDTO,
  ActionResult,
  resendIntro,
  reviveLead,
  createPaymentLink,
  markLinkDelivered,
  ignoreLead,
  PaymentLinkResponse,
} from "../../services/LeadsService";

interface ActionDef {
  key: string;
  label: string;
  run: () => void;
}

const errorMessage = (status: number, fallback?: string): string => {
  if (status === 409)
    return "Lead is in the wrong state for this action — refresh and try again.";
  if (status === 400) return "Customer isn't eligible to purchase.";
  if (status === 404) return "Lead not found.";
  if (status === 401) return "Session expired — please sign in again.";
  return fallback || "Action failed.";
};

const LeadActions = ({
  lead,
  onChanged,
}: {
  lead: AdminLeadDTO;
  onChanged: () => Promise<void> | void;
}): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [confirmIgnore, setConfirmIgnore] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const copyToClipboard = async (url: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(msg, { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't copy link", { variant: "error" });
    }
  };

  // Run an action endpoint, then refetch so the row reflects true server state.
  // (A payment-link send returning 200 does NOT guarantee delivery — a failed
  // send leaves the lead FAIL — so we never optimistically set the row; we re-read.)
  const run = async (
    fn: () => Promise<ActionResult<unknown>>,
    successMsg: string,
    afterOk?: (data: unknown) => void
  ) => {
    setBusy(true);
    try {
      const result = await fn();
      if (result.ok) {
        if (afterOk) afterOk(result.data);
        enqueueSnackbar(successMsg, { variant: "success" });
        // Await the refresh so the row stays busy until it lands — otherwise a
        // fast second click could fire another send before the list re-reads.
        await onChanged();
      } else {
        enqueueSnackbar(errorMessage(result.status, result.message), {
          variant: "error",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  // send:true only confirms the link exists, not that WhatsApp delivery succeeded
  // (it can still end in FAIL) — keep these toasts neutral; the refreshed delivery
  // chip is the source of truth for whether it actually went out.
  const doCreateLinkSend = () =>
    run(() => createPaymentLink(lead.id, true), "Payment link created");

  // Create a link without messaging, then copy it so it can be pasted manually.
  // run() reports "Link created"; copyToClipboard reports the copy result separately
  // (so a clipboard failure doesn't hide behind a success toast).
  const doCreateLinkNoMsg = () =>
    run(
      () => createPaymentLink(lead.id, false),
      "Link created",
      (d) => {
        const url = (d as PaymentLinkResponse)?.shortUrl;
        if (url) copyToClipboard(url, "Link copied");
      }
    );

  const doCopyLink = () => {
    if (lead.link?.shortUrl) copyToClipboard(lead.link.shortUrl, "Link copied");
  };

  // Build the action set for this row: one primary CTA + overflow menu items.
  const build = (): { primary: ActionDef | null; menu: ActionDef[] } => {
    const linkLive = lead.link?.linkLive ?? false;

    switch (lead.status) {
      case "CREATED":
        return {
          primary: {
            key: "link-send",
            label: "Create link + send",
            run: doCreateLinkSend,
          },
          menu: [
            {
              key: "link-nomsg",
              label: "Create link (no message)",
              run: doCreateLinkNoMsg,
            },
            {
              key: "intro",
              label: "Send intro again",
              run: () => run(() => resendIntro(lead.id), "Intro re-sent"),
            },
          ],
        };
      case "LINK_CREATED": {
        if (!linkLive) {
          return {
            primary: {
              key: "newlink-send",
              label: "New link + send",
              run: () =>
                run(
                  () => createPaymentLink(lead.id, true),
                  "New payment link created"
                ),
            },
            menu: [
              {
                key: "newlink-nomsg",
                label: "New link (no message)",
                run: doCreateLinkNoMsg,
              },
            ],
          };
        }
        // Not yet delivered — PENDING_MANUAL (chose manual) or FAIL (auto-send failed).
        const needsDelivery =
          lead.linkDelivery === "PENDING_MANUAL" ||
          lead.linkDelivery === "FAIL";
        const menu: ActionDef[] = [];
        if (needsDelivery) {
          menu.push({
            key: "mark-sent",
            label: "Mark as sent",
            run: () => run(() => markLinkDelivered(lead.id), "Marked as sent"),
          });
        }
        return {
          primary: {
            key: "send",
            label: !needsDelivery
              ? "Resend message"
              : lead.linkDelivery === "FAIL"
              ? "Retry send"
              : "Send now",
            run: () =>
              run(() => createPaymentLink(lead.id, true), "Send requested"),
          },
          menu,
        };
      }
      case "IGNORED":
        return {
          primary: {
            key: "revive",
            label: "Revive",
            run: () => run(() => reviveLead(lead.id), "Lead revived"),
          },
          menu: [],
        };
      case "FULFILLED":
      default:
        return { primary: null, menu: [] };
    }
  };

  const { primary, menu } = build();
  const canIgnore = lead.status === "CREATED" || lead.status === "LINK_CREATED";
  const menuItems = [...menu];
  if (canIgnore) {
    menuItems.push({
      key: "ignore",
      label: "Ignore",
      run: () => setConfirmIgnore(true),
    });
  }

  if (!primary && menuItems.length === 0) {
    return <>—</>;
  }

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {busy ? (
        <CircularProgress size={18} />
      ) : (
        <>
          {primary && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={primary.run}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              {primary.label}
            </Button>
          )}

          {lead.link?.shortUrl && lead.link.linkLive && (
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={doCopyLink}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {menuItems.length > 0 && (
            <>
              <Tooltip title="More">
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
              >
                {menuItems.map((item) => (
                  <MenuItem
                    key={item.key}
                    onClick={() => {
                      closeMenu();
                      item.run();
                    }}
                    sx={
                      item.key === "ignore"
                        ? { color: "error.main" }
                        : undefined
                    }
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </>
      )}

      <ConfirmationDialog
        open={confirmIgnore}
        handleClose={() => setConfirmIgnore(false)}
        heading="Ignore lead"
        description="Move this lead to Ignored? You can revive it later."
        cancelLabel="Cancel"
        confirmLabel="Ignore"
        onProceed={() => {
          setConfirmIgnore(false);
          run(() => ignoreLead(lead.id), "Lead ignored");
        }}
      />
    </Box>
  );
};

export default LeadActions;
